/* Native theme interactions. Product prices/availability are always rendered by Shopify. */
(() => {
  const announcementRoots = new WeakSet();
  function initializeAnnouncements(scope = document) {
    scope.querySelectorAll('[data-announcements]').forEach(banner => {
      if (announcementRoots.has(banner)) return;
      announcementRoots.add(banner);
      const slides = [...banner.querySelectorAll('[data-announcement-slide]')];
      const control = banner.querySelector('[data-announcement-pause]');
      if (slides.length < 2 || banner.dataset.rotate !== 'true') return;
      const motion = matchMedia('(prefers-reduced-motion: reduce)');
      let index = 0, paused = false, hovered = false, timer;
      const interval = Math.max(4000, Number(banner.dataset.interval) || 6000);
      function render() {
        banner.toggleAttribute('data-enhanced', !motion.matches);
        control.hidden = motion.matches;
        slides.forEach((slide, i) => {
          slide.classList.toggle('is-active', i === index);
          slide.setAttribute('aria-hidden', String(!motion.matches && i !== index));
        });
      }
      function schedule() {
        clearTimeout(timer);
        if (!banner.isConnected || motion.matches || paused || hovered || banner.contains(document.activeElement) || document.hidden) return;
        timer = setTimeout(() => {
          slides.forEach(slide => slide.classList.remove('is-leaving'));
          slides[index].classList.add('is-leaving');
          index = (index + 1) % slides.length;
          render();
          schedule();
        }, interval);
      }
      control.addEventListener('click', () => {
        paused = !paused;
        control.setAttribute('aria-label', paused ? control.dataset.resumeLabel : control.dataset.pauseLabel);
        control.querySelector('span').textContent = paused ? '▶' : 'Ⅱ';
        schedule();
      });
      banner.addEventListener('mouseenter', () => { hovered = true; schedule(); });
      banner.addEventListener('mouseleave', () => { hovered = false; schedule(); });
      banner.addEventListener('focusin', schedule);
      banner.addEventListener('focusout', () => queueMicrotask(schedule));
      const updateMotion = () => { render(); schedule(); };
      motion.addEventListener('change', updateMotion);
      document.addEventListener('visibilitychange', schedule);
      banner.addEventListener('gg:dispose', () => {
        clearTimeout(timer);
        motion.removeEventListener('change', updateMotion);
        document.removeEventListener('visibilitychange', schedule);
      }, { once: true });
      render();
      schedule();
    });
  }
  initializeAnnouncements();
  document.addEventListener('shopify:section:load', event => initializeAnnouncements(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-announcements]').forEach(banner => banner.dispatchEvent(new Event('gg:dispose')));
  });
  function filterGear(section) {
    const stock = section.querySelector('[data-stock-filter]').value;
    const tab = section.querySelector('[data-category-tab][aria-selected="true"]');
    const category = tab.dataset.categoryTab;
    let visible = 0;
    section.querySelectorAll('[data-gear-item]').forEach(card => {
      card.hidden = (stock !== 'all' && (card.dataset.available === 'true') !== (stock === 'available')) || (category === 'All gear' ? card.hasAttribute('data-exclude-all-gear') : card.dataset.category !== category);
      if (!card.hidden) visible++;
    });
    const count = section.querySelector('[data-gear-count]');
    count.textContent = (visible === 1 ? count.dataset.countOne : count.dataset.countOther).replace('__count__', String(visible));
    section.querySelector('[data-empty-filter]').hidden = visible !== 0;
    section.querySelector('[data-gear-panel]').setAttribute('aria-labelledby', tab.id);
  }
  function selectGearTab(tab) {
    const section = tab.closest('section');
    section.querySelectorAll('[data-category-tab]').forEach(button => {
      button.setAttribute('aria-selected', String(button === tab));
      button.tabIndex = button === tab ? 0 : -1;
    });
    filterGear(section);
  }
  document.addEventListener('keydown', event => {
    const tab = event.target.closest('[data-category-tab]');
    if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const tabs = [...tab.closest('[role="tablist"]').querySelectorAll('[data-category-tab]')];
    const index = tabs.indexOf(tab);
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
    selectGearTab(tabs[next]);
    tabs[next].focus();
  });
  function initializeVideos(scope = document) {
    scope.querySelectorAll('[data-video-frame]').forEach(frame => {
      if (frame.dataset.initialized) return;
      frame.dataset.initialized = 'true';
      const video = frame.querySelector('video');
      video.hidden = true;
      frame.querySelector('[data-video-play]').hidden = false;
      video.addEventListener('error', () => { frame.querySelector('[data-video-error]').hidden = false; });
    });
  }
  initializeVideos();
  document.addEventListener('shopify:section:load', event => initializeVideos(event.target));
  const cardObservers = new WeakMap();
  function initializeCardImages(scope = document) {
    scope.querySelectorAll('template[data-card-image]').forEach(template => {
      const link = template.parentElement;
      if (cardObservers.has(link)) return;
      const reveal = () => {
        template.replaceWith(template.content.cloneNode(true));
        cardObservers.get(link)?.disconnect();
        cardObservers.delete(link);
      };
      if (!('IntersectionObserver' in window)) { reveal(); return; }
      const observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) reveal();
      }, { rootMargin: '300px 0px', threshold: 0 });
      cardObservers.set(link, observer);
      observer.observe(link);
    });
  }
  initializeCardImages();
  // Keep secondary photos inert until a mouse or keyboard user explores a card.
  function loadCardHover(link) {
    const template = link?.querySelector('template[data-card-hover]');
    if (!template) return;
    const image = template.content.querySelector('img').cloneNode(true);
    const ready = () => link.classList.add('has-hover-image');
    image.addEventListener('load', ready, { once: true });
    image.addEventListener('error', () => image.remove(), { once: true });
    template.replaceWith(image);
    if (image.complete && image.naturalWidth > 0) ready();
  }
  document.addEventListener('pointerover', event => {
    if (event.pointerType !== 'mouse' || !window.matchMedia('(any-hover: hover)').matches) return;
    loadCardHover(event.target.closest('.product-image-link'));
  });
  document.addEventListener('focusin', event => {
    const link = event.target.closest('.product-image-link');
    if (link?.matches(':focus-visible')) loadCardHover(link);
  });
  document.addEventListener('shopify:section:load', event => initializeCardImages(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('template[data-card-image]').forEach(template => {
      cardObservers.get(template.parentElement)?.disconnect();
      cardObservers.delete(template.parentElement);
    });
  });
  const galleryCleanup = new WeakMap();
  function initializeGallery(scope = document) {
    scope.querySelectorAll('.product-gallery').forEach(gallery => {
      if (galleryCleanup.has(gallery)) return;
      const strip = gallery.querySelector('.product-thumbnails');
      const main = gallery.querySelector('.product-main-photo img');
      if (!strip || !main) return;
      const controller = new AbortController();
      let observer, started = false;
      function reveal(button) {
        const template = button?.querySelector('template[data-gallery-thumbnail]');
        if (template) template.replaceWith(template.content.cloneNode(true));
      }
      function start() {
        if (started || !gallery.isConnected) return;
        started = true;
        const buttons = strip.querySelectorAll('[data-gallery-src]');
        if (!('IntersectionObserver' in window)) {
          buttons.forEach(reveal);
          return;
        }
        observer = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (!entry.isIntersecting) return;
            reveal(entry.target);
            observer.unobserve(entry.target);
          });
        }, { root: strip, rootMargin: '0px 68px', threshold: 0 });
        buttons.forEach(button => observer.observe(button));
      }
      strip.addEventListener('focusin', event => reveal(event.target.closest('[data-gallery-src]')), { signal: controller.signal });
      galleryCleanup.set(gallery, () => {
        controller.abort();
        observer?.disconnect();
        galleryCleanup.delete(gallery);
      });
      if (main.complete) start();
      else {
        main.addEventListener('load', start, { once: true, signal: controller.signal });
        main.addEventListener('error', start, { once: true, signal: controller.signal });
      }
    });
  }
  initializeGallery();
  document.addEventListener('shopify:section:load', event => initializeGallery(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('.product-gallery').forEach(gallery => galleryCleanup.get(gallery)?.()));
  function selectGalleryPhoto(gallery, index) {
    const thumbs = [...gallery.querySelectorAll('[data-gallery-src]')];
    const thumb = thumbs[index];
    if (!thumb) return;
    gallery.dataset.galleryIndex = String(index);
    const photoRatio = Number(thumb.dataset.galleryWidth) / Number(thumb.dataset.galleryHeight);
    const photoButton = gallery.querySelector('.product-main-photo');
    photoButton.style.setProperty('--gg-photo-width', `${320 * photoRatio}px`);
    photoButton.style.setProperty('--gg-photo-ratio', String(photoRatio));
    gallery.querySelectorAll('.product-main-photo img, .photo-dialog img').forEach(img => {
      img.width = Number(thumb.dataset.galleryWidth);
      img.height = Number(thumb.dataset.galleryHeight);
      img.sizes = img.closest('.photo-dialog') ? '90vw' : thumb.dataset.gallerySizes;
      img.srcset = thumb.dataset.gallerySrcset;
      img.src = thumb.dataset.gallerySrc;
      img.alt = thumb.dataset.galleryAlt;
    });
    thumbs.forEach(button => button.setAttribute('aria-pressed', String(button === thumb)));
    const count = gallery.querySelector('[data-photo-count]');
    count.textContent = count.dataset.template.replace('__index__', String(index + 1)).replace('__total__', String(thumbs.length));
    const position = gallery.querySelector('[data-photo-position]');
    if (position) position.textContent = `${index + 1} / ${thumbs.length}`;
    gallery.querySelectorAll('[data-gallery-step]').forEach(button => {
      button.disabled = Number(button.dataset.galleryStep) < 0 ? index === 0 : index === thumbs.length - 1;
    });
  }
  document.addEventListener('keydown', event => {
    const dialog = event.target.closest('.photo-dialog[open]');
    if (!dialog || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
    event.preventDefault();
    const gallery = dialog.closest('.product-gallery');
    selectGalleryPhoto(gallery, Number(gallery.dataset.galleryIndex) + (event.key === 'ArrowRight' ? 1 : -1));
  });
  const stickyCleanup = new WeakMap();
  function initializeStickyBuy(scope = document) {
    const roots = scope.matches?.('[data-product-root]') ? [scope] : scope.querySelectorAll('[data-product-root]');
    roots.forEach(root => {
      stickyCleanup.get(root)?.();
      const bar = root.querySelector('[data-sticky-buy]');
      const buy = root.querySelector('.product-buy');
      if (!bar || !buy) return;
      const observer = new IntersectionObserver(entries => {
        bar.hidden = entries[0].boundingClientRect.bottom > 0;
      });
      // Request a fresh asynchronous entry after scroll jumps or history restores.
      // Reading geometry here forced a full layout during initial product rendering.
      const updateVisibility = () => {
        observer.unobserve(buy);
        observer.observe(buy);
      };
      observer.observe(buy);
      document.addEventListener('scrollend', updateVisibility);
      window.addEventListener('hashchange', updateVisibility);
      window.addEventListener('pageshow', updateVisibility);
      stickyCleanup.set(root, () => {
        observer.disconnect();
        document.removeEventListener('scrollend', updateVisibility);
        window.removeEventListener('hashchange', updateVisibility);
        window.removeEventListener('pageshow', updateVisibility);
      });
    });
  }
  initializeStickyBuy();
  document.addEventListener('shopify:section:load', event => initializeStickyBuy(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-product-root]').forEach(root => stickyCleanup.get(root)?.()));
  let restockObserver;
  function initializeRestockButton() {
    const inline = document.querySelector('[data-open-restock]');
    if (!inline) return;
    inline.hidden = !document.querySelector('.gw-float-widget');
    if (!inline.hidden) restockObserver?.disconnect();
  }
  initializeRestockButton();
  if (document.querySelector('[data-open-restock][hidden]')) {
    restockObserver = new MutationObserver(initializeRestockButton);
    restockObserver.observe(document.body, { childList: true, subtree: true });
  }
  const dialogTriggers = new WeakMap();
  function openDialog(dialog, trigger) {
    if (!dialog) return;
    dialogTriggers.set(dialog, trigger);
    dialog.showModal();
    if (trigger.hasAttribute('aria-expanded')) trigger.setAttribute('aria-expanded','true');
    document.body.style.overflow = 'hidden';
    dialog.addEventListener('close', () => {
      document.body.style.overflow = '';
      const trigger = dialogTriggers.get(dialog);
      if (trigger?.hasAttribute('aria-expanded')) trigger.setAttribute('aria-expanded','false');
      trigger?.focus({ preventScroll: true });
    }, { once: true });
  }
  document.addEventListener('click', event => {
    const storyLink = event.target.closest('[data-story-gear]');
    if (storyLink) storyLink.closest('dialog').close();
    const menuLink = event.target.closest('#gg-menu a');
    if (menuLink) menuLink.closest('dialog').close();
    const target = event.target.closest('button, dialog');
    if (!target) return;
    if (target.matches('[data-open-restock]')) document.querySelector('.gw-float-widget')?.click();
    if (target.matches('[data-category-tab]')) selectGearTab(target);
    if (target.matches('[data-open-menu]')) openDialog(document.getElementById('gg-menu'), target);
    if (target.matches('[data-close-dialog]')) target.closest('dialog')?.close();
    if (target.matches('dialog') && event.target === target) target.close();
    if (target.matches('[data-dismiss-announcement]')) {
      const banner = target.closest('.announcement');
      banner.dispatchEvent(new Event('gg:dispose'));
      banner.remove();
    }
    if (target.matches('[data-zoom]')) openDialog(target.closest('[data-product-root]').querySelector('dialog'), target);
    if (target.matches('[data-gallery-src]')) {
      const gallery = target.closest('.product-gallery');
      selectGalleryPhoto(gallery, [...gallery.querySelectorAll('[data-gallery-src]')].indexOf(target));
    }
    if (target.matches('[data-gallery-step]')) {
      const gallery = target.closest('.product-gallery');
      selectGalleryPhoto(gallery, Number(gallery.dataset.galleryIndex) + Number(target.dataset.galleryStep));
    }
    if (target.matches('[data-video-play]')) {
      const frame = target.closest('[data-video-frame]');
      const video = frame.querySelector('video');
      target.hidden = true;
      video.hidden = false;
      video.focus({ preventScroll: true });
      video.play().catch(() => { frame.querySelector('[data-video-error]').hidden = false; });
    }
    if (target.matches('[data-about]')) openDialog(document.getElementById('gg-about-dialog'), target);
    if (target.matches('.feature button[aria-controls]')) {
      const expanded = target.getAttribute('aria-expanded') !== 'true';
      target.setAttribute('aria-expanded', String(expanded));
      target.querySelector('svg path')?.setAttribute('d', expanded ? 'M5 12h14' : 'M5 12h14M12 5v14');
      const panel = document.getElementById(target.getAttribute('aria-controls'));
      if (panel) { panel.inert = !expanded; panel.setAttribute('aria-hidden', String(!expanded)); panel.dataset.open = String(expanded); }
    }
  });
  document.addEventListener('change', async event => {
    const input = event.target;
    if (input.matches('[data-cart-form] input[name="updates[]"]')) {
      const form = input.closest('form');
      const progress = form.querySelector('[data-shipping-progress]');
      if (progress) progress.hidden = true;
      form.querySelector('[data-cart-dirty]').hidden = false;
    }
    if (input.matches('[data-stock-filter]')) filterGear(input.closest('section'));
    if (input.matches('[data-auto-submit]')) input.form.requestSubmit();
    if (input.matches('[data-option-value-id]')) {
      const root = input.closest('[data-product-root]');
      const values = [...root.querySelectorAll('[data-option-value-id]:checked')].map(el => el.dataset.optionValueId);
      const url = new URL(root.dataset.productUrl, location.origin);
      url.searchParams.set('section_id', root.dataset.sectionId);
      url.searchParams.set('option_values', values.join(','));
      root.querySelectorAll('button[name="add"], [data-sticky-add]').forEach(button => button.disabled = true);
      root.setAttribute('aria-busy', 'true');
      const request = (Number(root.dataset.request) || 0) + 1; root.dataset.request = request;
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error('Variant response failed');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const next = doc.querySelector('[data-product-root]');
        if (!next) throw new Error('Missing product section');
        if (Number(root.dataset.request) !== request) return;
        const previousVariant = root.querySelector('input[name="id"]');
        const nextVariant = next.querySelector('input[name="id"]');
        if (previousVariant?.disabled || nextVariant?.disabled) {
          url.searchParams.delete('section_id');
          if (nextVariant?.value) { url.searchParams.set('variant', nextVariant.value); url.searchParams.delete('option_values'); }
          location.assign(url);
          return;
        }
        // Keep third-party reviews and app widgets mounted during a variant change.
        const reviews = root.querySelector('[data-review-summary]');
        if (reviews) next.querySelector('[data-review-summary]')?.replaceWith(reviews);
        galleryCleanup.get(root.querySelector('.product-gallery'))?.();
        for (const selector of ['.product-gallery', '.product-summary']) {
          root.querySelector(selector)?.replaceWith(next.querySelector(selector));
        }
        root.removeAttribute('aria-busy');
        initializeGallery(root);
        initializeStickyBuy(root);
        root.querySelector(`[data-option-value-id="${input.dataset.optionValueId}"]`)?.focus({ preventScroll:true });
        const variant = root.querySelector('input[name="id"]')?.value;
        const address = new URL(location.href);
        if (variant) { address.searchParams.set('variant',variant); address.searchParams.delete('option_values'); }
        else { address.searchParams.delete('variant'); address.searchParams.set('option_values', values.join(',')); }
        history.replaceState({}, '', address);
      } catch {
        if (Number(root.dataset.request) !== request) return;
        // Navigate through Shopify to preserve correct purchase state if the section request fails.
        url.searchParams.delete('section_id'); location.assign(url);
      }
    }
    if (input.matches('#rag-priority')) {
      const choice = input.selectedOptions[0];
      const result = input.closest('.rag-finder').querySelector('.rag-result');
      const heading = document.createElement('h3'); heading.textContent = choice.dataset.title;
      const description = document.createElement('p'); description.textContent = choice.dataset.description;
      const link = document.createElement('a'); link.className = 'text-button'; link.href = choice.value; link.textContent = `Explore ${choice.dataset.title} →`;
      result.replaceChildren(heading, description, link);
    }
    if (input.matches('[data-fit-finder] select')) {
      const finder = input.closest('[data-fit-finder]');
      finder.querySelector('.height-fields').innerHTML = input.value === 'metric'
        ? '<label>Height (cm)<input type="number" min="90" max="250" step="0.01" inputmode="decimal" aria-describedby="fit-help fit-result"></label>'
        : '<label>Feet<input type="number" min="3" max="8" step="1" inputmode="numeric" aria-describedby="fit-help fit-result"></label><label>Inches<input type="number" min="0" max="11" step="1" inputmode="numeric" placeholder="0" aria-describedby="fit-help fit-result"></label>';
      finder.querySelector('.fit-result').textContent = '';
    }
  });
  document.addEventListener('submit', event => {
    const finder = event.target.closest('[data-fit-finder]');
    if (!finder) return;
    event.preventDefault();
    const fields = [...finder.querySelectorAll('.height-fields input')];
    const metric = finder.querySelector('select').value === 'metric';
    const first = Number(fields[0].value); const second = Number(fields[1]?.value || 0);
    const valid = fields[0].value !== '' && (metric ? first >= 90 && first <= 250 : Number.isInteger(first) && first >= 3 && first <= 8 && Number.isInteger(second) && second >= 0 && second <= 11);
    fields.forEach(field => field.setAttribute('aria-invalid',String(!valid)));
    const result = finder.querySelector('.fit-result');
    if (!valid) { result.textContent='Please enter a height from 3 to 8 feet with whole inches from 0 to 11, or 90–250 cm.'; return; }
    const height = metric ? first / 2.54 : first * 12 + second;
    const sizes = [...finder.closest('#liner-sizing').querySelectorAll('[data-liner-size]')].map(row => ({name:row.dataset.name,max:Number(row.dataset.max),length:Number(row.dataset.length),oz:Number(row.dataset.oz),grams:Number(row.dataset.grams),shoulder:Number(row.dataset.shoulder)})).filter(size => size.max > 0).sort((a,b) => a.max-b.max);
    const size = sizes.find(size => height <= size.max + 1e-6);
    if (!sizes.length) { result.textContent='Sizing information is being updated. Please contact support@glacialgear.com for help choosing.'; return; }
    if (!size) { result.innerHTML='<h4>Your height exceeds our published size range.</h4><p>Please <a href="mailto:support@glacialgear.com">contact us about fit</a> before ordering; we can’t recommend a listed size from height alone.</p>'; return; }
    result.innerHTML=`<h4>${size.name.replace(/[&<>"']/g, char => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[char]))} is your height-based starting point.</h4><p>Fits heights up to ${Math.floor(size.max/12)}′${size.max%12}″. Approximate liner length: ${size.length} in. Weight: ${size.oz} oz (${size.grams} g).</p><p>${height >= size.max-1 ? 'You’re within an inch of this size’s published height limit. If you want extra length or sleep with your toes pointed, contact us to talk through the fit before choosing.' : 'Check the shoulder measurement and fit notes below before choosing your size.'} Published shoulder circumference for this size: ${size.shoulder} in.</p><a class="text-button">View liner sizes &amp; colors →</a>`;
    const productLink = finder.dataset.productUrl;
    if (productLink && productLink.startsWith('/') && !productLink.startsWith('//')) result.querySelector('a').setAttribute('href', productLink);
    else result.querySelector('a').remove();
  });
})();
